import { CommonModule, DatePipe } from '@angular/common';
import { LoaderService } from '../../../../core/services/loader.service';
import { Component, computed, inject, OnInit, signal, effect, untracked, ChangeDetectionStrategy } from '@angular/core';
import { ApiService } from '../../../../core/services/api.service';
import { DatePickerComponent, DateRangePickerOutput, ComparisonType, DateRangePickerValue } from '../../../../shared/components/date-picker/date-picker.component';
import { LineChartComponent } from '../../../../shared/components/charts/line-chart/line-chart.component';
import { CommonService } from '../../../../core/services/common.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { FormsModule } from '@angular/forms';
import { VISIONBOARD_CURRENCY_OPTIONS, getVisionboardCurrencyInfo } from '../../visionboard/shared/visionboard-currency.config';
import { forkJoin, catchError, of } from 'rxjs';
import { BarLineComponent, BarChartData, LineConfig, LegendData, TooltipConfig } from '../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';
import { NationChartComponent } from '../../../../shared/components/charts/nation-chart/nation-chart.component';
import { GeographicMapComponent } from '../../../../shared/components/charts/geographic-map/geographic-map.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { MultiStackedBarChartComponent, StackedBarItem, StackedBarLegend } from '../../../../shared/components/charts/bar-charts/multi-stacked-bar-chart/multi-stacked-bar-chart.component';
import { HorizontalBarChartComponent, BarData } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

@Component({
  standalone: true,
  selector: 'app-fm-pipeline-portfolio',
  templateUrl: './fm-pipeline-portfolio.component.html',
  host: {
    class: 'block'
  },
  imports: [
    CommonModule,
    FormsModule,
    DatePickerComponent,
    BarLineComponent,
    GeographicMapComponent,
    IndividualToasterComponent,
    MultiStackedBarChartComponent,
    HorizontalBarChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe]
})
export class FmPipelinePortfolioComponent implements OnInit {
  private apiService = inject(ApiService);
  public _commonService = inject(CommonService);
  private loaderService = inject(LoaderService);
  private productConfigService = inject(ProductConfigService);
  private unitService = inject(UnitSuggestionService);

  isLoading = signal(false);
  isChartsLoading = signal(false);

  isAnyLoading = computed(() =>
    this.isChartsLoading() ||
    this.isGeoLoading() ||
    this.isIndustryLoading() ||
    this.isRetentionLoading() ||
    this.isTableLoading() ||
    this.isChartLoading() ||
    this.isRevenueLoading()
  );

  initialLoadDone = false;

  // Date State
  datesReady = false;
  lastDateRangeKey = '';
  skipNextEffectFetch = false;
  start_date = '';
  end_date = '';
  prev_start_date = '';
  prev_end_date = '';
  comparisonType: ComparisonType = 'none';
  unit = signal<'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year'>('day');
  isAllTimeSelected = signal<boolean>(false);

  is90DaysSelected(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    // Typical 90 days filter logic sets date range to roughly 89 or 90 days diff.
    const diff = end.diff(start, 'day');
    return diff >= 89 && diff <= 91;
  }

  currencyOptions = VISIONBOARD_CURRENCY_OPTIONS;
  selectedCurrency = signal<string>('1');
  selectedCurrencyInfo = computed(() => getVisionboardCurrencyInfo(this.selectedCurrency()));

  productConfig = computed(() => {
    const productId = this.apiService.selectedProductId();
    if (!productId) return null;
    return this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
  });

  // Data signals
  enrolledBusinessesSignal = signal<any>({ value: 0, netNew: 0, avgMo: 0, mom: 0 });
  businessesWithContractSignal = signal<any>({ value: 0, toConvert: 0, goalPct: 0 });

  // Charts
  chart1Data = signal<BarChartData[]>([]);
  chart1Colors = { data1: '#5eead4' as const };
  chart1Lines: LineConfig[] = [
    { key: 'contracts', label: 'Active Businesses', axis: 'right', color: '#a855f7' }
  ];
  chart1Legend: LegendData[] = [
    { label: 'Businesses Enrolled', type: 'square' as any, color: '#5eead4' },
    { label: 'Active Businesses', type: 'line' as any, color: '#a855f7' }
  ];
  chart1Tooltip: TooltipConfig[] = [
    { key: 'enrolled', label: 'Businesses Enrolled', type: 'number' },
    { key: 'contracts', label: 'Active Businesses', type: 'number' },
  ];

  chart2Data = signal<BarChartData[]>([]);
  chart2Colors = { data1: '#a855f7' as const };
  chart2Lines: LineConfig[] = [
    { key: 'financed', label: 'Financed ($)', axis: 'right', color: '#009688' }
  ];
  chart2Legend: LegendData[] = [
    { label: 'Contracts Created', type: 'square' as any, color: '#a855f7' },
    { label: 'Financed ($)', type: 'line' as any, color: '#009688' }
  ];
  chart2Tooltip: TooltipConfig[] = [
    { key: 'contracts', label: 'Contracts Created', type: 'number' },
    { key: 'financed', label: 'Financed ($)', type: 'currency' },
  ];

  // Proposal conversion
  proposalConversionSent = signal<number>(0);
  proposalConversionOpened = signal<number>(0);
  proposalConversionSelected = signal<number>(0);
  proposalConversionFinanced = signal<number>(0);
  proposalConversionChartData = signal<BarData[]>([]);

  proposalConversionOpenedPct = computed(() => {
    const sent = this.proposalConversionSent();
    const opened = this.proposalConversionOpened();
    return sent > 0 ? Math.round((opened / sent) * 100) : 0;
  });

  proposalConversionSentToFinancedPct = computed(() => {
    const sent = this.proposalConversionSent();
    const financed = this.proposalConversionFinanced();
    return sent > 0 ? Math.round((financed / sent) * 100) : 0;
  });

  // Geography & Industry State
  isGeoLoading = signal(false);
  geoMetricMode = signal<'biz' | 'con'>('biz');
  geoIndustryMode = signal<'0' | '1' | '2' | '3'>('0'); // 0: total, 1: dental, 2: hc, 3: non-hc

  geoConcentration = signal({
    topPercentage: 0,
    top1Percentage: 0,
    spread: 0,
    emerging: 0,
    top2Text: 'US + CA',
    topMarket: 'USA',
    topCount: 0
  });
  portfolioSignal = signal({ market: '', growth: 0 });

  mapData = signal<any[]>([]);
  mapValueKeys = signal({ keyName: 'state', valueKey: 'businessCount' });
  currentMapTotal = signal<number>(0);

  // Deep Dive Modal
  isDeepDiveModalOpen = signal(false);
  deepDiveData = signal<any>(null);

  // No Leads Modal
  // isNoLeadsModalOpen = signal(false);
  // noLeadsData = signal<any>(null);

  // Industry Data
  isIndustryLoading = signal(false);
  selectedVertical = signal<'0' | '1' | '2'>('0');
  topCategoriesData = signal<BarChartData[]>([]);
  categoryChartColors = { data1: '#009688' as const };
  categoryChartTooltip: TooltipConfig[] = [
    { key: 'contracts', label: 'Total Contracts', type: 'number' },
  ];

  riskQuadrantData = signal<any>({ q1: [], q2: [], q3: [], q4: [] });

  // Retention Data
  isRetentionLoading = signal(false);
  retentionMetrics = signal({
    sameDayCancelPct: 0,
    fixableChurnCount: 0,
    nextDayCancelPct: 0,
    sameDayCompletionPct: 0
  });

  topBusinessesList = signal<any[]>([]);
  anchorRiskData = signal<any>(null);
  totalBookContracts = signal<number>(0);
  anchorRisk = computed(() => {
    const rawData = this.anchorRiskData();
    if (rawData && rawData.top_account) {
      return {
        topBusinessName: rawData.top_account.business_name || 'Crown Distributors',
        topBusinessContracts: rawData.top_account.contract_count || 0,
        topAccountsCount: rawData.top_n || 6,
        topAccountsSharePct: rawData.top_n_share_percent || 0,
        headline: rawData.headline || ''
      };
    }

    const list = this.topBusinessesList();
    if (!list || list.length === 0) {
      return {
        topBusinessName: 'Crown Distributors',
        topBusinessContracts: 0,
        topAccountsCount: 6,
        topAccountsSharePct: 38,
        headline: ''
      };
    }

    const sorted = [...list].sort((a, b) => {
      const valA = Number(a.total_contracts !== undefined ? a.total_contracts : (a.contracts !== undefined ? a.contracts : (a.contract_count !== undefined ? a.contract_count : (a.count !== undefined ? a.count : (a.value !== undefined ? a.value : 0)))));
      const valB = Number(b.total_contracts !== undefined ? b.total_contracts : (b.contracts !== undefined ? b.contracts : (b.contract_count !== undefined ? b.contract_count : (b.count !== undefined ? b.count : (b.value !== undefined ? b.value : 0)))));
      return valB - valA;
    });

    const topItem = sorted[0];
    const topBusinessName = topItem?.business_name || topItem?.business || topItem?.name || topItem?.company_name || 'Crown Distributors';
    const topBusinessContracts = Number(topItem?.total_contracts !== undefined ? topItem?.total_contracts : (topItem?.contracts !== undefined ? topItem?.contracts : (topItem?.contract_count !== undefined ? topItem?.contract_count : (topItem?.count !== undefined ? topItem?.count : (topItem?.value !== undefined ? topItem?.value : 0)))));

    const topN = Math.min(6, sorted.length);
    const topAccountsCount = topN;
    const topNContractsSum = sorted.slice(0, topN).reduce((sum, item) => {
      const val = Number(item.total_contracts !== undefined ? item.total_contracts : (item.contracts !== undefined ? item.contracts : (item.contract_count !== undefined ? item.contract_count : (item.count !== undefined ? item.count : (item.value !== undefined ? item.value : 0)))));
      return sum + val;
    }, 0);

    let totalContracts = this.totalBookContracts();
    if (!totalContracts || totalContracts < topNContractsSum) {
      const sumAll = sorted.reduce((sum, item) => {
        const val = Number(item.total_contracts !== undefined ? item.total_contracts : (item.contracts !== undefined ? item.contracts : (item.contract_count !== undefined ? item.contract_count : (item.count !== undefined ? item.count : (item.value !== undefined ? item.value : 0)))));
        return sum + val;
      }, 0);
      totalContracts = Math.max(totalContracts, sumAll);
    }

    const topAccountsSharePct = totalContracts > 0 ? Math.round((topNContractsSum / totalContracts) * 100) : 38;

    return {
      topBusinessName: topBusinessName || 'Crown Distributors',
      topBusinessContracts: topBusinessContracts || 0,
      topAccountsCount: topAccountsCount || 6,
      topAccountsSharePct: Math.min(100, Math.max(0, topAccountsSharePct)),
      headline: ''
    };
  });

  disengagedAccounts = signal({
    dndCount: 842,
    dndPct: 11.1
  });

  // Terminal Enablement & Transactions Donuts
  trainingDoneDonut = signal([
    { label: 'Training done', value: 0, color: '#8b5cf6' },
    { label: 'Remaining', value: 100, color: '#f3f4f6' }
  ]);
  trainingDonePercent = signal<number>(0);
  trainingDoneTotal = signal<number>(0);
  trainingDoneCompleted = signal<number>(0);
  trainingDoneMom = signal<number>(0);
  trainingDoneIdle = computed(() => {
    const total = this.trainingDoneTotal();
    const done = this.trainingDoneCompleted();
    return done > 0 ? Math.round((total - done) / done) : 0;
  });

  rewardsSetDonut = signal([
    { label: 'Rewards set', value: 0, color: '#ea580c' },
    { label: 'Remaining', value: 100, color: '#f3f4f6' }
  ]);
  rewardsSetPercent = signal<number>(0);
  rewardsSetTotal = signal<number>(0);
  rewardsSetCompleted = signal<number>(0);

  paymentsDonut = signal([
    { label: 'Payments', value: 100, color: '#8b5cf6' }
  ]);

  // Terminal Transactions Data
  terminalTransactionAmount = signal<number>(0);
  terminalTransactionCount = signal<number>(0);
  terminalTransactionAvgTicket = signal<number>(0);
  terminalTransactionOneTime = signal<number>(0);
  terminalTransactionSubscription = signal<number>(0);
  terminalTransactionPlans = signal<number>(0);
  terminalTransactionNet = signal<number>(0);
  terminalTransactionMoM = signal<number>(0);

  // Terminal Readers Data
  terminalReaderApplied = signal<number>(0);
  terminalReaderActive = signal<number>(0);
  terminalReaderInQueue = signal<number>(0);
  terminalReaderDelivered = signal<number>(0);
  terminalReaderPaired = signal<number>(0);
  terminalReaderUnpaired = signal<number>(0);
  terminalReaderInactive = signal<number>(0);
  terminalReaderMoM = signal<number>(0);

  terminalReaderIdlePercentage = computed(() => {
    const active = this.terminalReaderActive();
    const inactive = this.terminalReaderInactive();
    const total = active + inactive;
    if (total === 0) return 0;
    return Math.round((inactive / total) * 100);
  });

  // Table & Chart Data
  isTableLoading = signal(false);
  contractStateSummary = signal<any[]>([]);
  contractStateHeadline = signal<string>('Verification, payment & dispute breakdown');
  contractTypesCountData = signal<any>({ total: 0, highestCount: 0, highestLabel: '', highestPercentage: 0 });

  isChartLoading = signal(false);
  contractStatusTrends = signal<StackedBarItem[]>([]);
  contractStatusCounts = signal({ month: '', active: 0, overdue: 0, completed: 0, cancelled: 0 });

  revenueStreamMixData = signal<StackedBarItem[]>([]);
  isRevenueLoading = signal(true);
  revenueLegend: StackedBarLegend[] = [
    { key: 'upfront', label: 'Upfront', color: '#8b5cf6' },
    { key: 'enrollment', label: 'Enrollment', color: '#3b82f6' },
    { key: 'downpayment', label: 'Downpayment', color: '#c084fc' },
    { key: 'lateFee', label: 'Late Fee', color: '#ea580c' }
  ];
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];

  statusChartLegend: StackedBarLegend[] = [
    { key: 'active', label: 'Active', color: '#8A2BE2' },
    { key: 'overdue', label: 'Overdue', color: '#D97706' },
    { key: 'completed', label: 'Completed', color: '#059669' },
    { key: 'cancelled', label: 'Cancelled', color: '#dc2626' }
  ];

  private getPayload() {
    const productId = this.apiService.selectedProductId();
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId!)
    );
    const payload: any = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      // is_dummy: 0,
      cocu_id: this.selectedCurrencyInfo().cocu_id,
      country_id: this.selectedCurrencyInfo().country_id,
      ...productConfig.payloadExtras
    };
    if (productConfig.productServerName === 'credee') {
      payload.timezone = 'PDT';
    }
    return payload;
  }

  private buildPayload(isPrev: boolean) {
    const p = this.getPayload();
    if (isPrev && this.comparisonType !== 'none' && this.prev_start_date) {
      p.start_date = this.prev_start_date;
      p.end_date = this.prev_end_date;
    }
    return p;
  }

  private dateRange() {
    return this.start_date && this.end_date ? { start: this.start_date, end: this.end_date } : null;
  }

  private getPreviousPayload() {
    if (this.comparisonType !== 'none' && this.prev_start_date && this.prev_end_date) {
      const payload = this.getPayload();
      payload.start_date = this.prev_start_date;
      payload.end_date = this.prev_end_date;
      return payload;
    }
    return null;
  }

  constructor() {
    effect(() => {
      if (this.isAnyLoading()) {
        if (!this.initialLoadDone) {
          untracked(() => this.loaderService.show());
        }
      } else {
        untracked(() => {
          this.loaderService.hide();
          this.initialLoadDone = true;
        });
      }
    });

    effect(() => {
      const pid = this.apiService.selectedProductId();
      if (pid && this.datesReady) {
        untracked(() => {
          if (this.skipNextEffectFetch) {
            this.skipNextEffectFetch = false;
            return;
          }
          this.fetchData();
        });
      }
    });
  }

  ngOnInit() {
    // Initial fetch driven by date picker
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    const rangeKey = [
      event.start_date,
      event.end_date,
      event.comparison_type,
      event.previous_start_date ?? '',
      event.previous_end_date ?? '',
      event.unit
    ].join('|');

    if (rangeKey === this.lastDateRangeKey) return;
    this.lastDateRangeKey = rangeKey;

    this.start_date = this.normalizeDate(event.start_date);
    this.end_date = this.normalizeDate(event.end_date);
    this.comparisonType = event.comparison_type;
    this.isAllTimeSelected.set(event.preset_label === 'All Time' || dayjs(event.end_date).diff(dayjs(event.start_date), 'year') >= 5);

    if (event.comparison_type !== 'none' && event.previous_start_date && event.previous_end_date) {
      this.prev_start_date = this.normalizeDate(event.previous_start_date);
      this.prev_end_date = this.normalizeDate(event.previous_end_date);
    } else {
      this.prev_start_date = '';
      this.prev_end_date = '';
    }

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date === this.end_date) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit || this.unitService.getUnitSuggestions(this.start_date, this.end_date).recommended as any);
    this.datesReady = true;
    this.skipNextEffectFetch = true;
    this.fetchData();
  }

  rangeValues(_event: DateRangePickerValue) { }

  onCurrencyChange(value: string) {
    if (value === this.selectedCurrency()) return;
    this.selectedCurrency.set(value);
    if (this.datesReady) {
      this.fetchData();
    }
  }

  private normalizeDate(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  isOneYearOrMore(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return end.diff(start, 'day') >= 364;
  }

  get formattedDateRange(): string {
    if (!this.start_date || !this.end_date) return '';
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
  }

  shouldEnableScroll(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    const diffYears = end.diff(start, 'year');
    const diffDays = end.diff(start, 'day');

    if (diffYears >= 3) return true;
    if (diffDays >= 29 && this.unit() === 'week') return true;
    if (diffDays >= 29 && this.unit() === 'day') return true;

    return false;
  }

  getChartMinWidth(dataLength: number): number | null {
    if (dataLength >= 12 || this.isOneYearOrMore() || this.shouldEnableScroll()) {
      if (dataLength < 8) {
        return 1200;
      } else if (dataLength < 15) {
        // Increase base scroll for smaller lengths like 90 days grouped by week (~13 weeks)
        return 2400;
      }
      // Increase width per item to ensure a substantial scroll
      return dataLength * 200;
    }
    return null;
  }

  getAllTimeChartMinWidth(dataLength: number): number | null {
    if (this.isAllTimeSelected() && dataLength > 0) {
      return Math.max(1200, dataLength * 100);
    }
    return null;
  }

  fetchData() {
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.datesReady) return;

    this.isLoading.set(true);

    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );

    const options = {
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    const payload = this.getPayload();
    const prevPayload = this.getPreviousPayload();

    this.isChartsLoading.set(true);

    const toolPath = 'visionboard';
    const listPayload = { ...payload, page: 1, limit: 1 };
    delete listPayload.cocu_id;
    delete listPayload.productId;
    delete listPayload.country_id;
    delete listPayload.overall;
    delete listPayload.timezone;

    let txListPayloadPrev: any = null;
    let implicitPrevPayload: any = null;
    if (prevPayload) {
      implicitPrevPayload = { ...prevPayload };
      txListPayloadPrev = { ...prevPayload, page: 1, limit: 1 };
    } else {
      const start = dayjs(this.start_date);
      const end = dayjs(this.end_date);
      const diffDays = end.diff(start, 'day');
      const prevStart = start.subtract(diffDays + 1, 'day').format('YYYY-MM-DD');
      const prevEnd = start.subtract(1, 'day').format('YYYY-MM-DD');

      implicitPrevPayload = { ...payload, start_time: prevStart, end_time: prevEnd };
      txListPayloadPrev = { ...implicitPrevPayload, page: 1, limit: 1 };
    }

    delete txListPayloadPrev.cocu_id;
    delete txListPayloadPrev.productId;
    delete txListPayloadPrev.country_id;
    delete txListPayloadPrev.overall;
    delete txListPayloadPrev.timezone;

    const datePayload = {
      ...payload,
      start_date: payload.start_time || this.start_date,
      end_date: payload.end_time || this.end_date
    };
    delete datePayload.start_time;
    delete datePayload.end_time;

    const timePayload = {
      ...payload,
      start_time: payload.start_date || this.start_date,
      end_time: payload.end_date || this.end_date
    };
    delete timePayload.start_date;
    delete timePayload.end_date;

    const requests: any = {
      overall: this.apiService.post(`${toolPath}/business_contracts_overall`, timePayload, options).pipe(catchError(() => of(null))),
      runningTotal: this.apiService.post(`${toolPath}/business_enrolled_running_total`, timePayload, options).pipe(catchError(() => of(null))),
      momActivity: this.apiService.post(`${toolPath}/business_contracts_mom_activity`, timePayload, options).pipe(catchError(() => of(null))),
      activeBizActivity: this.apiService.post(`${toolPath}/active_business_stats`, timePayload, options).pipe(catchError(() => of(null))),
      financedAmountByPeriod: this.apiService.post(`${toolPath}/financed_amount_by_period`, datePayload, options).pipe(catchError(() => of(null))),
      proposalConversion: this.apiService.post(`${toolPath}/proposal_funnel_overall`, datePayload, options).pipe(catchError(() => of(null))),
      contractTypesCount: this.apiService.post(`${toolPath}/contract_types_count`, timePayload, options).pipe(catchError(() => of(null))),
    };

    let prevTimePayload: any = null;
    if (implicitPrevPayload) {
      prevTimePayload = {
        ...implicitPrevPayload,
        start_time: implicitPrevPayload.start_time || implicitPrevPayload.start_date,
        end_time: implicitPrevPayload.end_time || implicitPrevPayload.end_date
      };
      delete prevTimePayload.start_date;
      delete prevTimePayload.end_date;

      requests['overallPrev'] = this.apiService.post(`${toolPath}/business_contracts_overall`, prevTimePayload, options).pipe(catchError(() => of(null)));
    }

    forkJoin(requests).subscribe({
      next: (results: any) => {
        // console.log('ACTIVE BIZ:', results.activeBizActivity);
        this.processOverallData(results.overall?.data, results.overallPrev?.data, results.runningTotal?.data || []);
        this.processMomActivityData(results.momActivity?.data || [], results.activeBizActivity?.data || []);
        this.processFinancedAmountByPeriodData(results.financedAmountByPeriod);
        this.processProposalConversionData(results.proposalConversion);
        this.processContractTypesCount(results.contractTypesCount?.data || results.contractTypesCount);
        const txData = results.terminalTransactions?.data || results.terminalTransactions?.records || results.terminalTransactions;
        const txDataPrev = results.terminalTransactionsPrev?.data || results.terminalTransactionsPrev?.records || results.terminalTransactionsPrev;

        this.processTerminalTransactionsData(txData, txDataPrev);
        this.processTerminalReadersData(
          txData?.readers || results.terminalReaders?.data || results.terminalReaders?.records || results.terminalReaders,
          txDataPrev?.readers,
          txData,
          txDataPrev
        );
        this.isChartsLoading.set(false);
      },
      error: () => {
        this.isChartsLoading.set(false);
      }
    });

    this.fetchGeoData();
    this.fetchIndustryData();
    this.fetchRetentionData();
    this.fetchContractStateSummary();
    this.fetchContractStatusTrends();
    this.fetchRevenueStreamMix();
  }

  // GEOGRAPHY DATA FETCHING
  setGeoMetricMode(mode: 'biz' | 'con') {
    this.geoMetricMode.set(mode);
    this.fetchMapData();
  }

  setGeoIndustryMode(mode: '0' | '1' | '2' | '3') {
    this.geoIndustryMode.set(mode);
    this.fetchMapData();
  }

  private fetchGeoData() {
    this.isGeoLoading.set(true);
    const dateRange = this.dateRange();
    if (!dateRange) return;

    const payload = this.buildPayload(false);
    const prevPayload = this.buildPayload(true);
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };
    const toolPath = 'visionboard';

    const requests: any = {
      stateFinanced: this.apiService.post(`${toolPath}/state_total_financed`, payload, options).pipe(catchError(() => of(null))),
    };

    if (prevPayload) {
      requests['stateFinancedPrev'] = this.apiService.post(`${toolPath}/state_total_financed`, prevPayload, options).pipe(catchError(() => of(null)));
    }

    forkJoin(requests).subscribe({
      next: (results: any) => {
        const rawCurrent = results.stateFinanced?.data || results.stateFinanced || [];
        const rawPrev = results.stateFinancedPrev?.data || results.stateFinancedPrev || [];
        const currentList = Array.isArray(rawCurrent) ? rawCurrent : (rawCurrent.states || rawCurrent.data || []);
        const prevList = Array.isArray(rawPrev) ? rawPrev : (rawPrev.states || rawPrev.data || []);
        this.processGeoKpis(currentList, prevList);
        this.fetchMapData();
      },
      error: () => {
        this.isGeoLoading.set(false);
      }
    });
  }

  private processGeoKpis(currentStates: any[], prevStates: any[]) {
    const getValue = (item: any) => Number(item.contract_count ?? item.total_financed ?? item.financed_amount ?? item.total ?? item.amount ?? 0);
    const getLabel = (item: any) => item.state_code || item.state || item.state_name || '';

    // 1. Geographic Concentration
    if (currentStates.length > 0) {
      const sorted = [...currentStates].sort((a, b) => getValue(b) - getValue(a));
      const topMarket = sorted[0];
      const allTotal = sorted.reduce((sum, c) => sum + getValue(c), 0);
      const top3Total = sorted.slice(0, 3).reduce((sum, c) => sum + getValue(c), 0);
      const topPercentage = allTotal > 0 ? (top3Total / allTotal) * 100 : 0;
      const top1Percentage = allTotal > 0 ? (getValue(topMarket) / allTotal) * 100 : 0;

      const top2Text = sorted.length > 1
        ? `${getLabel(sorted[0])} + ${getLabel(sorted[1])}`
        : `${getLabel(sorted[0])}`;

      const spread = 100 - Math.round(topPercentage);
      const emerging = Math.max(0, sorted.length - 3);

      this.geoConcentration.set({
        topPercentage: Math.round(topPercentage),
        top1Percentage: Math.round(top1Percentage),
        spread: spread,
        emerging: emerging,
        top2Text: top2Text,
        topMarket: getLabel(topMarket) || 'USA',
        topCount: getValue(topMarket)
      });

      // 2. Portfolio Signal (Period-over-Period growth of top market financed amount)
      if (prevStates.length > 0) {
        const prevTopMarket = prevStates.find(c => getLabel(c) === getLabel(topMarket));
        const prevTotal = prevTopMarket ? getValue(prevTopMarket) : 0;
        const currentTotal = getValue(topMarket);
        let growth = 0;
        if (prevTotal > 0) {
          growth = ((currentTotal - prevTotal) / prevTotal) * 100;
        } else if (currentTotal > 0) {
          growth = 100;
        }
        this.portfolioSignal.set({
          market: getLabel(topMarket) || 'USA',
          growth: growth
        });
      } else {
        this.portfolioSignal.set({ market: getLabel(topMarket) || 'USA', growth: 0 });
      }
    } else {
      this.geoConcentration.set({ topPercentage: 0, top1Percentage: 0, spread: 0, emerging: 0, top2Text: '-', topMarket: '-', topCount: 0 });
      this.portfolioSignal.set({ market: '-', growth: 0 });
    }
  }

  private fetchMapData() {
    this.isGeoLoading.set(true);
    const payload: any = this.buildPayload(false);
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };

    payload.country_id = 1; // US
    payload.metric = this.geoMetricMode() === 'biz' ? 'businesses' : 'contracts';
    payload.industryType = this.geoIndustryMode();

    this.apiService.post('visionboard/state_total_financed', payload, options)
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: (result: any) => {
          const rawList = result?.data || result || [];
          const stateList = Array.isArray(rawList) ? rawList : (rawList.states || rawList.data || []);
          this.processStateMapData(stateList);
          this.isGeoLoading.set(false);
        },
        error: () => {
          this.isGeoLoading.set(false);
        }
      });
  }

  private processStateMapData(stateList: any[]) {
    let total = 0;
    const isBiz = this.geoMetricMode() === 'biz';

    const mapped: any[] = stateList
      .filter((s: any) => s.state || s.state_name || s.state_code)
      .map((s: any) => {
        const stateName = s.state || s.state_name || s.state_code || '';
        const value = Number(s.value ?? (isBiz ? s.business_count : s.contract_count) ?? 0);
        total += value;
        return {
          state: stateName,
          country: 'United States',
          country_code: 'US',
          value: value
        };
      });

    this.mapData.set(mapped);
    this.currentMapTotal.set(total);
    this.mapValueKeys.set({ keyName: 'state', valueKey: 'value' });
  }

  onRegionClick(event: any) {
    const regionName = event.feature?.properties?.name || 'Unknown Region';
    const countryCode = event.feature?.properties?._countryCode || 'US';
    const countryLabel = countryCode === 'US' ? 'United States' : (countryCode === 'GB' ? 'United Kingdom' : (countryCode === 'AU' ? 'Australia' : countryCode));
    const isBizMode = this.geoMetricMode() === 'biz';

    const payload: any = this.buildPayload(false);
    payload.country_id = 1;
    payload.metric = isBizMode ? 'businesses' : 'contracts';
    payload.industryType = this.geoIndustryMode();
    payload.state = regionName;

    this.apiService.post('visionboard/state_total_financed', payload, { showLoader: true, showErrorToaster: true, productServerName: this.productConfig()?.productServerName || 'credee' })
      .subscribe({
        next: (res: any) => {
          const data = res?.data || res || {};

          let totalBiz = data.total_businesses || 0;
          let totalCon = data.total_contracts || 0;
          const avgContracts = totalBiz > 0 ? (totalCon / totalBiz) : 0;
          const kpis = data.kpis || {};
          const activeBiz = kpis.activeBusinesses || 0;
          const last30 = kpis.last30Days || 0;
          const last7 = kpis.last7Days || 0;
          const peak = data.trend_summary?.peak || 0;

          const trend = data.trend || [];
          let sparklineData = trend.map((t: any) => t.count || 0);
          if (sparklineData.length === 0) sparklineData = Array.from({ length: 30 }, () => 0);

          const svgWidth = 300;
          const svgHeight = 64;
          const xStep = sparklineData.length > 1 ? svgWidth / (sparklineData.length - 1) : svgWidth;
          const sparklinePath = sparklineData.map((val: number, i: number) => {
            const x = i * xStep;
            const y = peak > 0 ? svgHeight - (val / peak * svgHeight * 0.9) : svgHeight;
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(' ');
          const sparklineArea = `${sparklinePath} L ${svgWidth},${svgHeight} L 0,${svgHeight} Z`;

          const topBusinesses = data.businessContractPerformance || [];
          const financedTotal = kpis.top8Financed || topBusinesses.reduce((s: any, b: any) => s + (b.financed_amount || 0), 0);

          const industryModes = { '0': 'All industries', '1': 'Dental', '2': 'Healthcare', '3': 'Non-Healthcare' };
          const segLabel = (industryModes as any)[this.geoIndustryMode()] || 'All industries';

          this.deepDiveData.set({
            regionName,
            countryLabel,
            isBizMode,
            segLabel,
            totalBiz,
            activeBiz,
            totalCon,
            last30,
            last7,
            avgContracts,
            peak,
            sparklineData,
            sparklinePath,
            sparklineArea,
            topBusinesses,
            financedTotal
          });

          this.isDeepDiveModalOpen.set(true);
          document.body.style.overflow = 'hidden';
        },
        error: () => { }
      });
  }

  closeDeepDiveModal() {
    this.isDeepDiveModalOpen.set(false);
    document.body.style.overflow = '';
  }

  private processOverallData(overall: any, overallPrev: any, runningTotal: any[]) {
    // Businesses Enrolled KPI
    const totalEnrolled = overall?.total_businesses || 0;
    const totalEnrolledPrev = overallPrev?.total_businesses || 0;

    let momEnrolled = 0;
    let momEnrolledText = "0.00%";
    if (totalEnrolledPrev > 0) {
      momEnrolled = ((totalEnrolled - totalEnrolledPrev) / totalEnrolledPrev) * 100;
      momEnrolledText = `${momEnrolled > 0 ? '+' : ''}${momEnrolled.toFixed(2)}%`;
    }

    let netNew = 0;
    if (runningTotal && runningTotal.length) {
      netNew = runningTotal.reduce((sum, r) => sum + (r.new_enrollments || 0), 0);
    }
    const monthsDiff = dayjs(this.end_date).diff(dayjs(this.start_date), 'month') || 1;
    const avgMo = netNew / monthsDiff;

    this.enrolledBusinessesSignal.set({
      value: totalEnrolled,
      netNew,
      avgMo,
      mom: momEnrolled,
      momText: momEnrolledText,
      startFormatted: dayjs(this.start_date).format('MMM'),
      endFormatted: dayjs(this.end_date).format('MMM')
    });

    // Businesses with a Contract KPI
    const businessesWithContracts = overall?.businesses_with_contracts || 0;
    const toConvert = Math.abs(totalEnrolled - businessesWithContracts);

    let goalPct = 0;
    if (overall?.activation_rate_pct !== undefined && overall?.activation_rate_pct !== null) {
      goalPct = overall.activation_rate_pct;
    } else {
      goalPct = Math.min(totalEnrolled > 0 ? (businessesWithContracts / totalEnrolled) * 100 : 0, 100);
    }

    this.businessesWithContractSignal.set({
      value: businessesWithContracts,
      toConvert,
      goalPct
    });
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      case 'day':
        if (start.isSame(end, 'month')) {
          return `${start.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      case 'week':
        return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
      case 'month':
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      case 'quarter':
        return `Q${start.quarter()} ${start.format('YYYY')}`;
      case 'year':
        return `${start.format('YYYY')}`;
      default:
        return `${start.format('DD MMM YYYY')}`;
    }
  }

  private formatQuarterString(dateStr: string): string {
    if (this.unit() === 'quarter' && typeof dateStr === 'string' && dateStr.match(/^\d{4}-Q[1-4]$/)) {
      const parts = dateStr.split('-');
      return `${parts[1]} ${parts[0]}`;
    }
    return dateStr;
  }

  private processMomActivityData(momData: any[], activeBizResponse: any) {
    if (!momData) return;

    // Safely extract the array from the active business response
    let activeBizData: any[] = [];
    if (Array.isArray(activeBizResponse)) {
      activeBizData = activeBizResponse;
    } else if (activeBizResponse && Array.isArray(activeBizResponse.unit_data)) {
      activeBizData = activeBizResponse.unit_data;
    } else if (activeBizResponse && Array.isArray(activeBizResponse.data)) {
      activeBizData = activeBizResponse.data;
    } else if (activeBizResponse && Array.isArray(activeBizResponse.records)) {
      activeBizData = activeBizResponse.records;
    } else if (activeBizResponse?.data?.unit_data && Array.isArray(activeBizResponse.data.unit_data)) {
      activeBizData = activeBizResponse.data.unit_data;
    }

    const chartData = momData.map((d: any, index: number) => {
      const a = activeBizData.find((ad: any) => (ad.start || ad.unit_date) === (d.start || d.unit_date)) || activeBizData[index] || {};
      const activeCount = Number(a.total_doctors || a.count || a.total || a.business_count || a.total_businesses || a.dental + a.healthcare + a.non_healthcare || 0);
      return {
        start: this.formatQuarterString(d.start),
        end: this.formatQuarterString(d.end),
        enrolled: d.total_registered || 0,
        contracts: activeCount
      };
    });
    this.chart1Data.set(chartData);
  }

  private processFinancedAmountByPeriodData(res: any) {
    if (!res) return;
    const raw = res?.data ?? res;
    const data: any[] =
      raw?.series ||
      res.records?.records ||
      res.records ||
      (Array.isArray(res) ? res : []);
    if (!Array.isArray(data)) return;
    const chartData = data.map((d: any) => ({
      start: this.formatQuarterString(d.start || d.unit_date || d.date || d.period),
      end: this.formatQuarterString(d.end || d.unit_date || d.date || d.period),
      contracts: d.contract_count ?? d.total_contracts ?? d.contracts ?? 0,
      financed: d.financed_amount ?? d.total_financed_amount ?? d.financed ?? 0
    }));
    this.chart2Data.set(chartData);
  }

  private processProposalConversionData(res: any) {
    if (!res) {
      this.proposalConversionChartData.set([]);
      return;
    }

    const responseBody = res.data ?? res;

    let sent = 0;
    let opened = 0;
    let selected = 0;
    let financed = 0;
    let chartData: BarData[] = [];

    // Prefer funnel array
    const funnelArray = responseBody?.funnel ?? (Array.isArray(responseBody) ? responseBody : null);
    if (Array.isArray(funnelArray) && funnelArray.length > 0) {
      funnelArray.forEach((item: any) => {
        const stage = String(item.stage || item.label || item.name || item.step || item.title || '').toLowerCase();
        const val = Number(item.count ?? item.value ?? item.total ?? 0) || 0;
        const color = item.color || undefined;

        if (stage.includes('sent') || stage.includes('request')) {
          sent = val;
          chartData.push({ label: item.stage || 'Requests Sent', value: val, color: color || '#7c3aed' });
        } else if (stage.includes('open')) {
          opened = val;
          chartData.push({ label: item.stage || 'Link Opened', value: val, color: color || '#06b6d4' });
        } else if (stage.includes('select') || stage.includes('plan')) {
          selected = val;
          chartData.push({ label: item.stage || 'Plan Selected', value: val, color: color || '#f59e0b' });
        } else if (stage.includes('financ') || stage.includes('contract')) {
          financed = val;
          chartData.push({ label: item.stage || 'Contracts Financed', value: val, color: color || '#10b981' });
        }
      });
    } else {
      // Fallback: read from summary object or flat object
      const summary = responseBody?.summary ?? responseBody;
      if (summary && typeof summary === 'object') {
        sent = Number(summary.requests_sent ?? summary.total_sent ?? summary.sent ?? summary.proposals_sent ?? summary.sent_count ?? 0) || 0;
        opened = Number(summary.link_opened ?? summary.total_opened ?? summary.opened ?? summary.proposals_opened ?? summary.opened_count ?? 0) || 0;
        selected = Number(summary.plan_selected ?? summary.total_selected ?? summary.selected ?? summary.plans_selected ?? summary.selected_count ?? 0) || 0;
        financed = Number(summary.contracts_financed ?? summary.total_financed ?? summary.financed ?? summary.proposals_financed ?? summary.financed_count ?? 0) || 0;
      }

      chartData = [
        { label: 'Requests Sent', value: sent, color: '#7c3aed' },
        { label: 'Link Opened', value: opened, color: '#06b6d4' },
        { label: 'Plan Selected', value: selected, color: '#f59e0b' },
        { label: 'Contracts Financed', value: financed, color: '#10b981' }
      ];
    }

    this.proposalConversionSent.set(sent);
    this.proposalConversionOpened.set(opened);
    this.proposalConversionSelected.set(selected);
    this.proposalConversionFinanced.set(financed);

    if (sent === 0 && opened === 0 && selected === 0 && financed === 0) {
      this.proposalConversionChartData.set([]);
      return;
    }

    chartData.sort((a, b) => b.value - a.value);
    this.proposalConversionChartData.set(chartData);
  }

  private processTerminalTransactionsData(data: any, prevData?: any) {
    if (!data) return;

    if (data.cards) {
      const cards = data.cards;

      const totalAmount = cards.total?.amount ?? 0;
      const count = cards.total?.count ?? 0;
      const avg = count > 0 ? (totalAmount / count) : 0;

      const oneTime = cards.pay_in_full?.count ?? 0;
      const subscription = cards.subscription?.count ?? 0;
      const plans = cards.pp?.count ?? 0;

      const net = data.stats?.net_amount ?? data.stats?.net_to_business ?? totalAmount;

      let mom = 0;
      if (prevData && prevData.cards) {
        const prevCards = prevData.cards;
        const prevTotalAmount = prevCards.total?.amount ?? 0;
        const prevCount = prevCards.total?.count ?? 0;
        const prevAvg = prevCount > 0 ? (prevTotalAmount / prevCount) : 0;

        if (prevTotalAmount > 0) {
          mom = Math.round(((totalAmount - prevTotalAmount) / prevTotalAmount) * 100);
        } else if (totalAmount > 0) {
          mom = 100;
        }
      } else {
        mom = Number(data.stats?.mom_growth ?? data.stats?.mom ?? 0) || this.terminalTransactionMoM();
      }

      this.terminalTransactionAmount.set(totalAmount);
      this.terminalTransactionCount.set(count);
      this.terminalTransactionAvgTicket.set(avg);
      this.terminalTransactionOneTime.set(oneTime);
      this.terminalTransactionSubscription.set(subscription);
      this.terminalTransactionPlans.set(plans);
      this.terminalTransactionNet.set(net);
      this.terminalTransactionMoM.set(mom);

      this.paymentsDonut.set([
        { label: 'One-Time', value: oneTime > 0 ? oneTime : (count === 0 ? 1 : 0), color: '#a855f7' },
        { label: 'Subscription', value: subscription > 0 ? subscription : (count === 0 ? 1 : 0), color: '#3b82f6' },
        { label: 'Plans', value: plans > 0 ? plans : (count === 0 ? 1 : 0), color: '#f97316' }
      ]);
      return;
    }

    // Attempting to extract relevant data gracefully based on typical dashboard data structures.
    // Ensure we handle arrays or objects securely.
    let transactions = Array.isArray(data) ? data : (data?.data || []);
    if (!Array.isArray(transactions)) {
      transactions = [];
    }

    if (transactions.length > 0 || data) {
      // Typically API might return stats in a separate object or we compute from list
      let stats = data?.stats || data;

      let totalAmount = Number(stats?.total?.amount ?? stats?.total_amount ?? stats?.amount ?? 0);
      let count = Number(stats?.total?.count ?? stats?.charges_count ?? stats?.total_transactions ?? stats?.count ?? transactions.length);
      let avg = count > 0 ? (totalAmount / count) : 0;

      let oneTime = Number(stats?.pay_in_full?.count ?? stats?.one_time ?? stats?.one_time_count ?? 0);
      let subscription = Number(stats?.subscription?.count ?? stats?.subscription ?? stats?.subscription_count ?? 0);
      let plans = Number(stats?.pp?.count ?? stats?.plans ?? stats?.plans_count ?? 0);
      let net = Number(stats?.net_amount ?? stats?.net_to_business ?? 0) || totalAmount;

      let mom = 0;
      if (prevData) {
        const prevStats = prevData?.stats || prevData;
        const prevTotalAmount = Number(prevStats?.total?.amount ?? prevStats?.total_amount ?? prevStats?.amount ?? 0);
        if (prevTotalAmount > 0) {
          mom = Math.round(((totalAmount - prevTotalAmount) / prevTotalAmount) * 100);
        } else if (totalAmount > 0) {
          mom = 100;
        }
      } else {
        mom = Number(stats?.mom_growth ?? stats?.mom ?? 0);
        if (mom === 0 && !stats?.mom_growth && !stats?.mom) {
          mom = this.terminalTransactionMoM();
        }
      }

      this.terminalTransactionAmount.set(totalAmount);
      this.terminalTransactionCount.set(count);
      this.terminalTransactionAvgTicket.set(avg);
      this.terminalTransactionOneTime.set(oneTime);
      this.terminalTransactionSubscription.set(subscription);
      this.terminalTransactionPlans.set(plans);
      this.terminalTransactionNet.set(net);
      this.terminalTransactionMoM.set(mom);

      this.paymentsDonut.set([
        { label: 'One-Time', value: oneTime > 0 ? oneTime : (count === 0 ? 1 : 0), color: '#a855f7' },
        { label: 'Subscription', value: subscription > 0 ? subscription : (count === 0 ? 1 : 0), color: '#3b82f6' },
        { label: 'Plans', value: plans > 0 ? plans : (count === 0 ? 1 : 0), color: '#f97316' }
      ]);
    }
  }

  private processTerminalReadersData(data: any, prevData?: any, fullTxData?: any, fullTxDataPrev?: any) {
    if (!data && !fullTxData) return;

    const source = fullTxData || data;
    const prevSource = fullTxDataPrev || prevData;

    if (source?.training) {
      this.trainingDoneTotal.set(source.training.total || 0);
      this.trainingDoneCompleted.set(source.training.done || 0);
      this.trainingDonePercent.set(source.training.percent || 0);
      this.trainingDoneDonut.set([
        { label: 'Training done', value: source.training.percent || 0, color: '#8b5cf6' },
        { label: 'Remaining', value: 100 - (source.training.percent || 0), color: '#f3f4f6' }
      ]);

      let tMom = 0;
      if (prevSource?.training) {
        tMom = (source.training.done || 0) - (prevSource.training.done || 0);
      } else {
        tMom = source.training.mom_growth || source.training.mom || 0;
      }

      this.trainingDoneMom.set(tMom);
    }
    if (source?.rewards) {
      this.rewardsSetTotal.set(source.rewards.total || 0);
      this.rewardsSetCompleted.set(source.rewards.set || 0);
      this.rewardsSetPercent.set(source.rewards.percent || 0);
      this.rewardsSetDonut.set([
        { label: 'Rewards set', value: source.rewards.percent || 0, color: '#ea580c' },
        { label: 'Remaining', value: 100 - (source.rewards.percent || 0), color: '#f3f4f6' }
      ]);
    }

    if (!data) return;

    // Defaulting to the existing signal value if not found in the response.
    const stats = data?.stats || data;
    const prevStats = prevData?.stats || prevData;

    const applied = Number(stats?.applied ?? stats?.total_applied ?? this.terminalReaderApplied());
    const activeCurrent = Number(stats?.active ?? stats?.active_count ?? this.terminalReaderActive());
    const inQueue = Number(stats?.inQueue ?? stats?.in_queue ?? stats?.queue_count ?? this.terminalReaderInQueue());
    const delivered = Number(stats?.delivered ?? stats?.delivered_count ?? this.terminalReaderDelivered());
    const paired = Number(stats?.paired ?? stats?.paired_count ?? this.terminalReaderPaired());
    const unpaired = Number(stats?.unpaired ?? stats?.unpaired_count ?? this.terminalReaderUnpaired());
    const inactive = Number(stats?.inactive ?? stats?.inactive_count ?? this.terminalReaderInactive());

    let mom = 0;
    if (prevData) {
      const activePrev = Number(prevStats?.active ?? prevStats?.active_count ?? 0);
      if (activePrev > 0) {
        mom = Math.round(((activeCurrent - activePrev) / activePrev) * 100);
      } else if (activeCurrent > 0) {
        mom = 100;
      }
    } else {
      mom = Number(stats?.mom_growth ?? stats?.mom ?? 0);
      if (mom === 0 && !stats?.mom_growth && !stats?.mom) {
        mom = this.terminalReaderMoM();
      }
    }

    this.terminalReaderApplied.set(applied);
    this.terminalReaderActive.set(activeCurrent);
    this.terminalReaderInQueue.set(inQueue);
    this.terminalReaderDelivered.set(delivered);
    this.terminalReaderPaired.set(paired);
    this.terminalReaderUnpaired.set(unpaired);
    this.terminalReaderInactive.set(inactive);
    this.terminalReaderMoM.set(mom);
  }


  // INDUSTRY DATA FETCHING
  setVerticalMode(mode: '0' | '1' | '2') {
    this.selectedVertical.set(mode);
    this.fetchTopCategoriesData();
  }

  private fetchIndustryData() {
    this.isIndustryLoading.set(true);
    const payload: any = this.buildPayload(false) || {};
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };

    const reqs: any = {
      type0: this.apiService.post(`visionboard/sub_industries_contracts`, { ...payload, type: 0 }, options).pipe(catchError(() => of(null))),
      type1: this.apiService.post(`visionboard/sub_industries_contracts`, { ...payload, type: 1 }, options).pipe(catchError(() => of(null))),
      type2: this.apiService.post(`visionboard/sub_industries_contracts`, { ...payload, type: 2 }, options).pipe(catchError(() => of(null))),
    };

    forkJoin(reqs).subscribe({
      next: (res: any) => {
        let allSpecialties: any[] = [];
        if (res.type0?.data) allSpecialties = [...allSpecialties, ...res.type0.data];
        if (res.type1?.data) allSpecialties = [...allSpecialties, ...res.type1.data];
        if (res.type2?.data) allSpecialties = [...allSpecialties, ...res.type2.data];

        this.processRiskQuadrant(allSpecialties);
      }
    });

    this.fetchTopCategoriesData();
  }

  private fetchTopCategoriesData() {
    const payload: any = this.buildPayload(false) || {};
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };

    this.apiService.post(`visionboard/trending_industries`, { ...payload, type: parseInt(this.selectedVertical()) }, options).subscribe({
      next: (res: any) => {
        let categoryData: any[] = [];
        if (res && res.data) {
          const vertical = this.selectedVertical();
          if (vertical === '0') {
            categoryData = res.data.dental || res.data.Dental || [];
          } else if (vertical === '1') {
            categoryData = res.data.healthcare || res.data.health_care || res.data.Healthcare || [];
          } else if (vertical === '2') {
            categoryData = res.data.non_hc || res.data.non_healthcare || res.data['non-hc'] || [];
          }

          if (!categoryData || !categoryData.length) {
            const arrays = Object.values(res.data).filter(val => Array.isArray(val)) as any[][];
            if (arrays.length === 1) {
              categoryData = arrays[0];
            } else if (arrays.length > 1) {
              categoryData = arrays[parseInt(vertical)] || [];
            }
          }
        }
        this.processTopCategories(categoryData || []);
        this.isIndustryLoading.set(false);
      },
      error: () => this.isIndustryLoading.set(false)
    });
  }

  private processTopCategories(data: any[]) {
    const sorted = data.sort((a, b) => {
      const valA = a.current_period_count !== undefined ? Number(a.current_period_count) : (a.total_contracts || 0);
      const valB = b.current_period_count !== undefined ? Number(b.current_period_count) : (b.total_contracts || 0);
      return valB - valA;
    }).slice(0, 5); // reduce from 6 to 5 or keep 6, let's just truncate strings

    const chartData = sorted.map((item: any) => {
      let label = item.speciality_name || 'Unknown';
      if (label.length > 15) {
        label = label.substring(0, 13) + '...';
      }
      const value = item.current_period_count !== undefined ? Number(item.current_period_count) : (item.total_contracts || 0);
      return {
        start: label,
        end: label,
        data1: value,
        contracts: value,
        category: label,
        fullCategory: item.speciality_name || 'Unknown'
      };
    });
    this.topCategoriesData.set(chartData);
  }

  private processRiskQuadrant(data: any[]) {
    const PROFIT_THRESHOLD = 3000;
    const RISK_THRESHOLD = 15;

    const q1: any[] = []; // High profit, Low risk
    const q2: any[] = []; // High profit, High risk
    const q3: any[] = []; // Moderate, Med risk
    const q4: any[] = []; // High overdue risk

    const sorted = data.sort((a, b) => b.total_contracts - a.total_contracts);

    const seenNames = new Set<string>();

    sorted.forEach((item: any) => {
      const name = item.speciality_name || 'Unknown';
      if (seenNames.has(name)) return;
      seenNames.add(name);

      const profit = item.average_financed_amount || 0;
      const risk = item.overdue_ratio || 0;

      const entry = {
        name: name,
        profit: profit,
        risk: risk
      };

      if (profit >= PROFIT_THRESHOLD && risk < RISK_THRESHOLD) {
        if (q1.length < 3) q1.push(entry);
      } else if (profit >= PROFIT_THRESHOLD && risk >= RISK_THRESHOLD) {
        if (q2.length < 3) q2.push(entry);
      } else if (profit < PROFIT_THRESHOLD && risk < RISK_THRESHOLD) {
        if (q3.length < 3) q3.push(entry);
      } else {
        if (q4.length < 3) q4.push(entry);
      }
    });

    this.riskQuadrantData.set({ q1, q2, q3, q4 });
  }

  // RETENTION DATA FETCHING
  private fetchRetentionData() {
    this.isRetentionLoading.set(true);
    const productId = this.apiService.selectedProductId();
    if (productId && this.datesReady) {
      const productConfig = this.productConfigService.getProductConfig(
        this.productConfigService.getProductName(productId)
      );
      const options = {
        showErrorToaster: false,
        showSuccessToaster: false,
        showLoader: false,
        cache: true,
        addAccessToken: productConfig.requiresAccessToken,
        productServerName: productConfig.productServerName,
      };
      const payload = this.getPayload();
      const toolPath = 'visionboard';

      this.apiService.post(`${toolPath}/businesses_by_contracts`, { ...payload, top_n: 6 }, options).pipe(
        catchError(() => of(null))
      ).subscribe({
        next: (res: any) => {
          this.processTopBusinessesData(res);
          this.isRetentionLoading.set(false);
        },
        error: () => {
          this.isRetentionLoading.set(false);
        }
      });

    } else {
      this.isRetentionLoading.set(false);
    }

    // As per the mapping document, the APIs for cancellation-timing buckets are missing.
    // Using mocked data to match Figma designs until the `tools/contract_lifecycle_buckets` API is ready.
    this.retentionMetrics.set({
      sameDayCancelPct: 0,
      fixableChurnCount: 0,
      nextDayCancelPct: 0,
      sameDayCompletionPct: 0
    });
  }

  private processTopBusinessesData(response: any) {
    let list: any[] = [];
    if (Array.isArray(response)) {
      list = response;
    } else if (response && Array.isArray(response.data)) {
      list = response.data;
    } else if (response && Array.isArray(response.records)) {
      list = response.records;
    } else if (response && Array.isArray(response.result)) {
      list = response.result;
    } else if (response && Array.isArray(response.top_businesses)) {
      list = response.top_businesses;
    } else if (response && response.data && Array.isArray(response.data.top_businesses)) {
      list = response.data.top_businesses;
    }

    // Save raw response to use top_account, headline, etc.
    const rawData = response?.data || response;
    this.anchorRiskData.set(rawData);

    this.topBusinessesList.set(list || []);
  }

  private processContractStatesBreakdown(response: any) {
    if (!response) return;

    const resData = response.data || response.result || response;

    if (resData.headline) {
      this.contractStateHeadline.set(resData.headline);
    } else if (response.headline) {
      this.contractStateHeadline.set(response.headline);
    }

    let list: any[] = [];
    if (Array.isArray(resData)) {
      list = resData;
    } else if (resData && Array.isArray(resData.rows)) {
      list = resData.rows;
    } else if (resData && Array.isArray(resData.data)) {
      list = resData.data;
    } else if (resData && Array.isArray(resData.records)) {
      list = resData.records;
    } else if (resData && Array.isArray(resData.breakdown)) {
      list = resData.breakdown;
    } else if (resData && Array.isArray(resData.list)) {
      list = resData.list;
    } else if (resData && typeof resData === 'object') {
      const ignoreKeys = ['stats', 'total', 'total_contracts', 'success', 'message', 'code', 'status', 'meta', 'unit_date', 'start_date', 'end_date', 'start_time', 'end_time', 'is_error', 'flag', 'is_prism_access'];
      list = Object.entries(resData)
        .filter(([key, val]) => !ignoreKeys.includes(key.toLowerCase()) && val !== null && val !== undefined && (typeof val === 'number' || typeof val === 'object' || !isNaN(Number(val))))
        .map(([key, val]) => {
          if (typeof val === 'number' || typeof val === 'string') {
            return { name: key, value: Number(val) || 0 };
          } else if (val && typeof val === 'object') {
            const countVal = Number((val as any).value !== undefined ? (val as any).value : ((val as any).count !== undefined ? (val as any).count : ((val as any).contracts !== undefined ? (val as any).contracts : ((val as any).total !== undefined ? (val as any).total : 0)))) || 0;
            return {
              name: (val as any).name || (val as any).state || (val as any).label || key,
              value: countVal,
              pct: (val as any).pct || (val as any).percentage || (val as any).share
            };
          }
          return { name: key, value: 0 };
        })
        .filter(r => r.value > 0 || r.name !== '');
    }

    let dndCount = 0;
    let dndPct = 0;

    if (list && list.length > 0) {
      const dndItem = list.find((item: any) => {
        const name = String(item?.name || item?.state || item?.label || item?.status || '').toLowerCase().trim();
        return name === 'dnd' || name.includes('disengaged') || name === 'do not disturb';
      });

      if (dndItem) {
        dndCount = Number(dndItem.value !== undefined ? dndItem.value : (dndItem.count !== undefined ? dndItem.count : (dndItem.contracts !== undefined ? dndItem.contracts : (dndItem.total !== undefined ? dndItem.total : 0)))) || 0;
        if (dndItem.percent_of_book !== undefined || dndItem.percentage !== undefined || dndItem.pct !== undefined || dndItem.share !== undefined) {
          dndPct = Number(dndItem.percent_of_book !== undefined ? dndItem.percent_of_book : (dndItem.percentage !== undefined ? dndItem.percentage : (dndItem.pct !== undefined ? dndItem.pct : (dndItem.share !== undefined ? dndItem.share : 0)))) || 0;
        }
      }

      if (!dndPct && dndCount > 0) {
        const sumAll = list.reduce((sum, item) => {
          const val = Number(item?.value !== undefined ? item.value : (item?.count !== undefined ? item.count : (item?.contracts !== undefined ? item.contracts : (item?.total !== undefined ? item.total : 0)))) || 0;
          return sum + val;
        }, 0);
        const total = Math.max(sumAll, this.totalBookContracts(), response?.stats?.value || 0, response?.total || 0, 1);
        dndPct = total > 0 ? (dndCount / total) * 100 : 11.1;
      }
    } else if (typeof response === 'object') {
      const dndObj = response.dnd || response.DND || response.disengaged || response.Disengaged;
      if (typeof dndObj === 'number') {
        dndCount = dndObj;
        const total = Math.max(Number(response.total || response.total_contracts || response?.stats?.value || 0), this.totalBookContracts(), 1);
        dndPct = total > 0 ? (dndCount / total) * 100 : 11.1;
      } else if (dndObj && typeof dndObj === 'object') {
        dndCount = Number(dndObj.value || dndObj.count || dndObj.contracts || dndObj.total || 0);
        dndPct = Number(dndObj.percent_of_book || dndObj.percentage || dndObj.pct || dndObj.share || 0);
        if (!dndPct && dndCount > 0) {
          const total = Math.max(Number(response.total || response.total_contracts || response?.stats?.value || 0), this.totalBookContracts(), 1);
          dndPct = total > 0 ? (dndCount / total) * 100 : 11.1;
        }
      }
    }

    this.disengagedAccounts.set({
      dndCount: dndCount || 0,
      dndPct: isNaN(dndPct) ? 0 : Number(dndPct.toFixed(1))
    });

    if (list && list.length > 0) {
      const total = response?.stats?.value || response?.total || response?.total_contracts || list.reduce((sum: number, r: any) => sum + (Number(r.value || r.count || r.contracts || 0) || 0), 0) || 1;

      const getSignal = (stateName: string) => {
        const s = String(stateName || '').toLowerCase().replace(/_/g, ' ').trim();
        if (s === 'verified' || s === 'healthy' || s === 'active' || s.includes('verif')) {
          return { text: '✓ Healthy', colorClass: 'text-[#15803d]' };
        }
        if (s === 'non verified' || s === 'unverified' || s === 'nonverified' || s === 'risk' || s.includes('non verif')) {
          return { text: '⚠ Risk', colorClass: 'text-[#b91c1c]' };
        }
        if (s === 'clear') {
          return { text: '✓ Clear', colorClass: 'text-[#15803d]' };
        }
        if (s === 'pause payment' || s === 'paused' || s === 'pause' || s.includes('cash flow') || s.includes('payment')) {
          return { text: '⚠ Cash flow', colorClass: 'text-[#b45309]' };
        }
        if (s === 'dnd' || s.includes('disengaged') || s.includes('disturb') || s === 'do not disturb') {
          return { text: '⚠ Disengaged', colorClass: 'text-[#b45309]' };
        }
        if (s === 'disputed' || s.includes('dispute') || s.includes('immaterial')) {
          return { text: 'Immaterial', colorClass: 'text-[#6a7c93]' };
        }
        if (s === 'overdue' || s === 'late' || s === 'defaulted' || s.includes('overdue')) {
          return { text: '⚠ Overdue Risk', colorClass: 'text-[#b91c1c]' };
        }
        if (s === 'completed' || s === 'paid off' || s === 'paid') {
          return { text: '✓ Completed', colorClass: 'text-[#15803d]' };
        }
        if (s === 'cancelled' || s === 'canceled' || s === 'churn') {
          return { text: '⚠ Cancelled', colorClass: 'text-[#6a7c93]' };
        }
        return { text: '• Normal', colorClass: 'text-slate-600 dark:text-slate-400' };
      };

      const formatStateName = (name: string) => {
        const raw = String(name || '').trim();
        if (raw.toLowerCase() === 'dnd') return 'DND';
        if (raw.toLowerCase() === 'non_verified' || raw.toLowerCase() === 'nonverified') return 'Non Verified';
        if (raw.toLowerCase() === 'pause_payment' || raw.toLowerCase() === 'pausepayment') return 'Pause Payment';
        return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      };

      const summary = list.map((r: any) => {
        const val = Number(r.value !== undefined ? r.value : (r.count !== undefined ? r.count : (r.contracts !== undefined ? r.contracts : 0))) || 0;
        const rawName = r.name || r.state || r.label || 'Unknown';
        const pctVal = r.pct !== undefined ? Number(r.pct) : (r.percent_of_book !== undefined ? Number(r.percent_of_book) : (r.percentage !== undefined ? Number(r.percentage) : (r.share !== undefined ? Number(r.share) : (val / total) * 100)));
        const signalVal = r.signal && typeof r.signal === 'string' ? r.signal : rawName;
        return {
          state: formatStateName(rawName),
          count: val,
          pct: pctVal || 0,
          signal: getSignal(signalVal)
        };
      });

      this.contractStateSummary.set(summary || []);
    }
  }

  // CONTRACT STATE SUMMARY TABLE
  private fetchContractStateSummary() {
    this.isTableLoading.set(true);
    const payload = this.buildPayload(false) || {};
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };
    const toolPath = 'visionboard';
    this.apiService.post(`${toolPath}/contract-state-summary`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        this.processContractStatesBreakdown(res);
        this.isTableLoading.set(false);
      },
      error: () => this.isTableLoading.set(false)
    });
  }

  // CONTRACT STATUS TRENDS CHART
  private fetchContractStatusTrends() {
    this.isChartLoading.set(true);
    const payload = this.getPayload();
    const datePayload = {
      ...payload,
      start_date: payload.start_time || this.start_date,
      end_date: payload.end_time || this.end_date
    };
    delete datePayload.start_time;
    delete datePayload.end_time;

    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };
    const toolPath = 'visionboard';

    this.apiService.post(`${toolPath}/contract_status_by_period`, datePayload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        if (!res) {
          this.contractStatusTrends.set([]);
          this.isChartLoading.set(false);
          return;
        }

        // API returns:
        const raw = res?.data ?? res;
        const data: any[] =
          raw?.months ||
          res.records?.records ||
          res.records ||
          (Array.isArray(res) ? res : []);

        if (!Array.isArray(data) || data.length === 0) {
          this.contractStatusTrends.set([]);
          this.isChartLoading.set(false);
          return;
        }

        const chartData: StackedBarItem[] = [];

        data.forEach((d: any) => {
          // API provides month_name  as the label
          let labelStr = d.month_name || d.month || d.start || d.unit_date || d.date;
          if (!labelStr) return;

          // If it's a date string (not a short month name), format it properly
          if (labelStr.length > 5) {
            const dateObj = dayjs(labelStr);
            if (dateObj.isValid()) {
              const isMultiYear = this.start_date && this.end_date && dayjs(this.start_date).year() !== dayjs(this.end_date).year();
              labelStr = isMultiYear ? dateObj.format('MMM DD, YYYY') : dateObj.format('MMM DD');
              if (this.unit() === 'year') {
                labelStr = dateObj.format('YYYY');
              } else if (this.unit() === 'month') {
                labelStr = dateObj.format('MMM YYYY');
              } else if (this.unit() === 'week') {
                const endStr = d.end || d.end_time;
                const endDate = endStr ? dayjs(endStr) : dateObj.add(6, 'day');
                labelStr = isMultiYear ? `${dateObj.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}` : `${dateObj.format('DD MMM')} - ${endDate.format('DD MMM')}`;
              } else if (this.unit() === 'quarter') {
                const qNum = Math.floor(dateObj.month() / 3) + 1;
                labelStr = `Q${qNum} ${dateObj.format('YYYY')}`;
              } else if (this.unit() === 'hour') {
                labelStr = dateObj.format('MMM DD, h A');
              }
            }
          }

          const activeVal = Number(d.active ?? d.active_contracts ?? d.total_active ?? d.count_active ?? d.data1 ?? 0);
          const overdueVal = Number(d.overdue ?? d.overdue_contracts ?? d.total_overdue ?? d.count_overdue ?? d.data2 ?? 0);
          const completedVal = Number(d.completed ?? d.completed_contracts ?? d.total_completed ?? d.count_completed ?? d.data3 ?? 0);
          const cancelledVal = Number(d.cancelled ?? d.canceled ?? d.cancelled_contracts ?? d.total_cancelled ?? d.count_cancelled ?? d.data4 ?? 0);

          chartData.push({
            label: labelStr,
            active: activeVal,
            overdue: overdueVal,
            completed: completedVal,
            cancelled: cancelledVal
          });
        });

        this.contractStatusTrends.set(chartData);

        const summary = raw?.summary || {};
        const monthStr = this.end_date ? dayjs(this.end_date).format('MMM') : dayjs().format('MMM');
        this.contractStatusCounts.set({
          month: monthStr,
          active: Number(summary.active ?? 0),
          overdue: Number(summary.overdue ?? 0),
          completed: Number(summary.completed ?? 0),
          cancelled: Number(summary.cancelled ?? summary.canceled ?? 0)
        });

        this.isChartLoading.set(false);
      },
      error: () => {
        this.contractStatusTrends.set([]);
        this.isChartLoading.set(false);
      }
    });
  }

  private fetchRevenueStreamMix() {
    this.isRevenueLoading.set(true);
    const payload = this.getPayload();
    const datePayload = {
      ...payload,
      start_date: payload.start_time || this.start_date,
      end_date: payload.end_time || this.end_date
    };
    delete datePayload.start_time;
    delete datePayload.end_time;

    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'credee' };
    const toolPath = 'visionboard';

    this.apiService.post(`${toolPath}/revenue_streams_by_period`, datePayload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        if (!res) {
          this.revenueStreamMixData.set([]);
          this.isRevenueLoading.set(false);
          return;
        }

        // API returns
        const raw = res?.data ?? res;
        const data: any[] =
          raw?.series ||
          res.records?.records ||
          res.records ||
          (Array.isArray(res) ? res : []);

        if (!Array.isArray(data) || data.length === 0) {
          this.revenueStreamMixData.set([]);
          this.isRevenueLoading.set(false);
          return;
        }

        const aggregated = new Map<string, any>();

        data.forEach((d: any) => {
          const dateStr = d.start || d.start_time || d.period || d.unit_date || d.date;
          const dateObj = dayjs(dateStr ? dateStr : undefined);
          const isMultiYear = this.start_date && this.end_date && dayjs(this.start_date).year() !== dayjs(this.end_date).year();
          let labelStr = isMultiYear ? dateObj.format('MMM DD, YYYY') : dateObj.format('MMM DD');
          if (this.unit() === 'year') {
            labelStr = dateObj.format('YYYY');
          } else if (this.unit() === 'month') {
            labelStr = dateObj.format('MMM YYYY');
          } else if (this.unit() === 'week') {
            const endStr = d.end || d.end_time;
            const endDate = endStr ? dayjs(endStr) : dateObj.add(6, 'day');
            labelStr = isMultiYear ? `${dateObj.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}` : `${dateObj.format('DD MMM')} - ${endDate.format('DD MMM')}`;
          } else if (this.unit() === 'quarter') {
            const qNum = Math.floor(dateObj.month() / 3) + 1;
            labelStr = `Q${qNum} ${dateObj.format('YYYY')}`;
          } else if (this.unit() === 'hour') {
            labelStr = dateObj.format('MMM DD, h A');
          }

          if (!aggregated.has(labelStr)) {
            aggregated.set(labelStr, { upfront: 0, enrollment: 0, downpayment: 0, lateFee: 0, timestamp: dateObj.valueOf() });
          }
          const current = aggregated.get(labelStr)!;
          current.upfront += parseFloat(d.upfront_fee ?? d.upfront_amount ?? d.upfront ?? d.total_upfront ?? 0) || 0;
          current.enrollment += parseFloat(d.enrollment_fee ?? d.enrollment_amount ?? d.enrollment ?? d.total_enrollment ?? 0) || 0;
          current.downpayment += parseFloat(d.downpayment_amount ?? d.downpayment ?? d.total_downpayment ?? 0) || 0;
          current.lateFee += parseFloat(d.late_fee ?? d.late_fee_amount ?? d.total_late_fee ?? d.lateFee ?? 0) || 0;
          current.timestamp = Math.min(current.timestamp, dateObj.valueOf());
        });

        const sortedMonths = Array.from(aggregated.entries()).sort((a, b) => a[1].timestamp - b[1].timestamp);

        const chartData: StackedBarItem[] = sortedMonths.map(([labelStr, values]) => ({
          label: labelStr,
          upfront: values.upfront,
          enrollment: values.enrollment,
          downpayment: values.downpayment,
          lateFee: values.lateFee
        }));

        this.revenueStreamMixData.set(chartData);
        this.isRevenueLoading.set(false);
      },
      error: () => {
        this.revenueStreamMixData.set([]);
        this.isRevenueLoading.set(false);
      }
    });
  }
  processContractTypesCount(response: any) {
    const data = response?.unit_data || response?.data?.unit_data || response?.data || response;

    if (!data || !Array.isArray(data) || data.length === 0) {
      this.contractTypesCountData.set({ total: 0, highestCount: 0, highestLabel: '', highestPercentage: 0 });
      return;
    }

    const total = data.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    let highestCount = 0;
    let highestLabel = '';

    data.forEach(item => {
      const count = Number(item.count) || 0;
      if (count > highestCount) {
        highestCount = count;
        highestLabel = item.contract_type_label || item.contract_type_name || '';
      }
    });

    const highestPercentage = total > 0 ? Math.round((highestCount / total) * 100) : 0;

    this.contractTypesCountData.set({
      total,
      highestCount,
      highestLabel,
      highestPercentage
    });
  }
}
